import { Component, OnInit } from '@angular/core';
import { AngularFirestore, DocumentData } from '@angular/fire/compat/firestore';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ConnectionsService } from '../../../services/connectionsWeb3/connections.service';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';

import * as jsonErc20 from '../../../abis/ERC20.json';
import { ProjectsDataService } from 'src/app/services/projects-data.service';

@Component({
  selector: 'app-info-dividends',
  templateUrl: './info-dividends.component.html',
  styleUrls: ['./info-dividends.component.css']
})
export class InfoDividendsComponent implements OnInit {
  workbook: XLSX.WorkBook = XLSX.utils.book_new();
  web3: any | undefined;
  abiToken: any = jsonErc20;
  form: FormGroup = this.createForm();
  availableTokens:any[]=[];
  loadingTokens: boolean = false;

  constructor(
    private connSrv: ConnectionsService,
    private projectsDataService: ProjectsDataService,
    private afs: AngularFirestore) {
    this.web3 = this.connSrv.web3Instance;
  }

  ngOnInit() {
    this.loadTokens();  
  }

  private loadTokens(){
    this.loadingTokens = true;
    console.log('🔍 [DEBUG] Cargando tokens desde Firestore...');
    
    this.projectsDataService.getProjects().subscribe({
      next: (projects) => {
        console.log('🔍 [DEBUG] Respuesta de API:', projects.length, 'proyectos encontrados');
        
        if (!projects || projects.length === 0) {
          console.warn('⚠️ [WARNING] No se encontraron proyectos');
          this.availableTokens = [];
          this.loadingTokens = false;
          return;
        }

        this.availableTokens = projects
          .map(data => {
            return {
              tokensAddress: data['tokensAddress'],
              nameTokens: data['nameTokens']
            };
          })
          .filter(token => {
            const isValid = token.tokensAddress && token.nameTokens;
            if (!isValid) {
              console.warn('⚠️ [WARNING] Token inválido encontrado:', token);
            }
            return isValid;
          });

        console.log('✅ [SUCCESS] Tokens cargados correctamente:', this.availableTokens.length);
        this.loadingTokens = false;
      },
      error: (error) => {
        console.error('❌ [ERROR] Error al cargar tokens desde API:', error);
        
        Swal.fire({
          title: 'Error al cargar proyectos',
          text: 'No se pudieron cargar los proyectos. Por favor, recarga la página o verifica tu conexión.',
          icon: 'error',
          confirmButtonText: 'Aceptar'
        });
        
        this.availableTokens = [];
        this.loadingTokens = false;
      }
    });
  }

  createForm() {
    return new FormGroup({
      tokenAddress: new FormControl(null, [Validators.required])
    })
  }

  get tokenAddress() { return this.form?.get('tokenAddress'); }

  async getExcel() {
    if (this.form.invalid) { 
      this.form.markAllAsTouched();
      return; 
    }

    console.log('🔍 [DEBUG] Starting Excel generation for token:', this.tokenAddress?.getRawValue());

    Swal.fire({
      title: 'Procesando...',
      html: 'No cierres esta pantalla!!',
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading()
      }
    });

    this.afs.collection('investments', ref => ref.where('tokensAddress', '==', this.tokenAddress?.getRawValue()).limit(1)).get().subscribe({
      next: async (snapDocs: any) => {
        console.log('🔍 [DEBUG] Investment query result:', snapDocs.docs.length, 'documents found');
        
        if (snapDocs.docs.length === 0) {
          console.error('❌ [ERROR] No investment found for token:', this.tokenAddress?.getRawValue());
          Swal.fire({
            title: 'Error',
            text: 'No se encontró información de inversión para este token',
            icon: 'error'
          });
          return;
        }

        const totalSupply = snapDocs.docs[0].data().amountToSell;
        console.log('🔍 [DEBUG] Total supply found:', totalSupply);
        this._getExcel(totalSupply);    
      },
      error: (error) => {
        console.error('❌ [ERROR] Error querying investments:', error);
        Swal.fire({
          title: 'Error',
          text: 'Error al obtener información de inversión',
          icon: 'error'
        });
      }
    });
  }

  async _getExcel(_totalSupply: number) {
    console.log('🔍 [DEBUG] Getting snapshots for token:', this.tokenAddress?.getRawValue());
    
    this.afs.collection('snapshots').doc(this.tokenAddress?.getRawValue()).get().subscribe({
      next: (snapDoc: any) => {
        console.log('🔍 [DEBUG] Snapshot query result:', snapDoc.exists);
        
        if (snapDoc.exists) {
          const values: any[] = Object.values(snapDoc.data() as any[]);
          console.log('🔍 [DEBUG] Snapshots found:', values.length);
          let _numFull1 = values.length;
        for (const value of values) {
          console.log('🔍 [DEBUG] Processing snapshot:', value);
          
          if (!value.snapShotId || value.snapShotId === '') {
            console.error('❌ [ERROR] Empty snapShotId found:', value);
            _numFull1--;
            continue;
          }
          
          console.log('🔍 [DEBUG] Querying w-invest for snapShotId:', value.snapShotId);
          this.afs.collection('w-invest').doc(this.tokenAddress?.getRawValue()).collection(value.snapShotId).get().subscribe({
            next: (snapDocs: any) => {
              console.log('🔍 [DEBUG] w-invest query result:', snapDocs.docs.length, 'documents found');
            if (snapDocs.docs.length > 0) {
              let _data: any[] = [];

              for (let i = 0; i < snapDocs.docs.length; i++) {
                Object.values(snapDocs.docs[i].data()).forEach((doc: any) => {
                  const _dividendosTotales = value.totalAmount;
                  const _totalSupplyTokens = _totalSupply;
                  const _beneficioBrutoPorEmpresa = doc.walletByCompany ? (this.web3.utils.fromWei(doc.balance, 'ether') * _dividendosTotales) / _totalSupplyTokens : 0;
                  const _beneficioNetoPorEmpresa = _beneficioBrutoPorEmpresa > 0 
                    ? _beneficioBrutoPorEmpresa - (_beneficioBrutoPorEmpresa * (doc.percentRetentionTo / 100)) 
                    : 0;
                  const _beneficioBruto = doc.walletByCompany === false ? (this.web3.utils.fromWei(doc.balance, 'ether') * _dividendosTotales) / _totalSupplyTokens : 0;
                  const _beneficioNeto = _beneficioBruto > 0
                    ? _beneficioBruto - (_beneficioBruto * (doc.percentRetentionTo / 100))
                    : 0;
                  _data.push({
                    ...doc,
                    beneficio_bruto_por_empresa: _beneficioBrutoPorEmpresa,
                    beneficio_neto_por_empresa: _beneficioNetoPorEmpresa,
                    beneficio_bruto: _beneficioBruto,
                    beneficio_neto: _beneficioNeto
                  });
                });
              }

              let _numFull2 = _data.length;
              for (let i = 0; i < _data.length; i++) {
                const uid = _data[i].uid;
                this.afs.collection('users').doc(uid).get().subscribe((snapDoc: any) => {
                  if (snapDoc.exists) {
                    _data[i] = {
                      email: snapDoc.data().email || "",
                      nombre_particular: snapDoc.data().particular?.nombre || "",
                      apellido1_particular: snapDoc.data().particular?.apellido1 || "",
                      apellido2_particular: snapDoc.data().particular?.apellido2 || "",
                      documentIdentification_particular: snapDoc.data().particular?.documentIdentification || "",
                      pais_residencia_particular: snapDoc.data().particular?.pais_de_residencia || "",
                      nacionalidad_particular: snapDoc.data().particular?.nacionalidad || "",
                      domicilio_particular: snapDoc.data().particular?.domicilio || "",
                      codigo_postal_particular: snapDoc.data().particular?.codigo_postal || "",
                      ciudad_particular: snapDoc.data().particular?.ciudad || "",
                      trabaja_funcion_publica_particular: snapDoc.data().particular?.trabaja_funcion_publica || "",
                      vive_en_usa_particular: snapDoc.data().particular?.vive_en_usa || "",
                      telefono_particular: snapDoc.data().particular?.telefono || "",
                      datos_empresa: JSON.stringify(snapDoc.data().Empresa?.Datos_Empresa) || "",
                      representante_empresa: JSON.stringify(snapDoc.data().Empresa?.Representante) || "",
                      intermediacion_compra_domoblock: _data[i].walletByCompany ? "Sí" : "No",
                      address_usada: _data[i].addressUsed || "",
                      cantidad_tokens: _data[i].balance || 0,
                      porcentaje_retencion: _data[i].percentRetentionTo || 0,
                      beneficio_bruto_por_empresa: _data[i].beneficio_bruto_por_empresa.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2}),
                      beneficio_neto_por_empresa: _data[i].beneficio_neto_por_empresa.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2}),
                      beneficio_bruto: _data[i].beneficio_bruto.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2}),
                      beneficio_neto: _data[i].beneficio_neto.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2}),
                      dai_eur: value.priceStableCoin || 0
                    };
                  }
                  _numFull2--;
                });
              }

              const _interval2 = setInterval(() => {
                if (_numFull2 === 0) {
                  clearInterval(_interval2);
                  const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(_data);
                  XLSX.utils.book_append_sheet(this.workbook, worksheet, `${value.snapShotId}_${this.tokenAddress?.getRawValue().substring(0, 12)}...`);
                  _numFull1--;
                }
              }, 1000);
            } else {
              console.log('🔍 [DEBUG] No documents found in w-invest for snapShotId:', value.snapShotId);
              _numFull1--;
            }
          },
          error: (error) => {
            console.error('❌ [ERROR] Error querying w-invest:', error);
            _numFull1--;
          }
        });
        }
        const _interval1 = setInterval(() => {
          if (_numFull1 === 0) {
            clearInterval(_interval1);
            console.log('🔍 [DEBUG] Generating Excel file...');
            XLSX.writeFile(this.workbook, 'info.xlsx');
            Swal.close();
          }
        }, 1000);
      } else {
        console.error('❌ [ERROR] No snapshots found for token:', this.tokenAddress?.getRawValue());
        Swal.fire({
          title: 'Error',
          text: 'No se encontraron snapshots para este token',
          icon: 'error'
        });
      }
    },
    error: (error) => {
      console.error('❌ [ERROR] Error getting snapshots:', error);
      Swal.fire({
        title: 'Error',
        text: 'Error al obtener snapshots',
        icon: 'error'
      });
    }
  });
  }
}

